import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { getPaymentPublishableKey } from '@/env';
import { apiRequest } from '@/lib/api-client';

import { refreshRegistrationDependentReads } from '../event-registrations/api';

import {
  MOCK_CARD_ENTRY_NOTICE,
  MOCK_CARD_FIELD_NOTICE,
  MOCK_PAYMENT_METHOD_TOKEN,
} from './constants';
import { createStripeCardElements } from './stripe-card-elements';
import type {
  CancellationPreviewResponse,
  CardElementsHost,
  CheckoutResponse,
  MyOrderResponse,
  MyRegisteredEventsResponse,
  PaymentResponse,
  ReceiptResponse,
} from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`).
 *
 * **Everything here needs a session.** `apiRequest` attaches a bearer token when one is in storage
 * and omits it otherwise, so a signed-out caller reaches these and is refused by the API —
 * correctly. The client's job is to not draw the control; the control is the server's.
 *
 * **No path carries a member id**, matching the API: the member is the session, so the queries are
 * keyed by event alone.
 *
 * ## Opening checkout is a mutation and reading it is a query, and that is not a naming choice
 *
 * Opening checkout **takes a seat off availability for fifteen minutes** (`EVT-022`). A `useQuery`
 * would let react-query refetch it on window focus, on reconnect, and on mount of any component
 * that happened to use the same key — every one of those holding somebody's seat. So it is a
 * `useMutation`, fired once by an explicit act, and the countdown reads the `GET`, which mints
 * nothing.
 */

/**
 * The cache key carries the chosen ticket type.
 *
 * A member switching from General to VIP is asking a different question of the same route — the
 * itemisation, the total and the pay control's label all change — so the two answers are two
 * cache entries. Keying on the event alone would show the previous tier's total for a beat after
 * the switch, on the one screen where a stale number is a member agreeing to the wrong amount.
 */
const checkoutKey = (eventId: string, ticketTypeId: string | null): readonly unknown[] => [
  'event-orders',
  'checkout',
  eventId,
  ticketTypeId,
];
const receiptKey = (eventId: string): readonly unknown[] => ['event-orders', 'receipt', eventId];

/**
 * The checkout this member already has open, or `{ checkout: null }`.
 *
 * `staleTime: 0` and no polling. The countdown does **not** re-fetch each second — it ticks
 * locally from the two fields the server served (see `secondsLeftOnHold`), because a request per
 * second per member at checkout would be a request per second per member at checkout. What this
 * query is for is the reload: a member who comes back to the page gets the hold that exists, with
 * the time it has left, rather than a fresh fifteen minutes.
 *
 * `retry: false` for the reason the registrations slice gives: a 401 is a final answer.
 */
export function useCheckout(
  eventId: string,
  options: { readonly enabled: boolean; readonly ticketTypeId: string | null },
): UseQueryResult<CheckoutResponse> {
  return useQuery({
    queryKey: checkoutKey(eventId, options.ticketTypeId),
    queryFn: () =>
      apiRequest<CheckoutResponse>(
        `/api/event-orders/events/${encodeURIComponent(eventId)}/checkout${ticketTypeQuery(options.ticketTypeId)}`,
      ),
    enabled: options.enabled && eventId.length > 0,
    retry: false,
  });
}

/**
 * `?ticketTypeId=…`, or nothing at all.
 *
 * The selection travels on the URL because **the seat hold does not carry it** — a hold holds a
 * seat, and a per-tier hold is the first row of an inventory `EVT-019` AC-3 forbids. Putting it
 * in the query string is what makes a reload, a back button and a pasted link all come back to
 * the ticket the member was actually looking at.
 */
function ticketTypeQuery(ticketTypeId: string | null): string {
  return ticketTypeId === null ? '' : `?ticketTypeId=${encodeURIComponent(ticketTypeId)}`;
}

/**
 * Open checkout — hold the seat and price the order.
 *
 * The API answers `409` for a full event, for one whose seats are all held, and for a member who
 * already holds a place, each with the sentence `event-registrations` chose. The screen surfaces
 * that message rather than composing its own, so what a member reads is what the server actually
 * applied — including in the case that matters, where the screen said there was room and the last
 * seat went between the render and the tap.
 *
 * Re-posting is safe: `EVT-022` hands back the live hold this member already has, with the time it
 * has left, rather than minting a fresh fifteen minutes.
 */
export function useOpenCheckout(
  eventId: string,
): UseMutationResult<{ checkout: CheckoutResponse['checkout'] }, Error, string | null> {
  const queryClient = useQueryClient();
  return useMutation({
    // The variable is which ticket type — `null` for the organizer's first, which is what the
    // pinned reserve bar named and priced. Re-posting with a different one is safe and does not
    // extend the hold: `EVT-022` hands back the live hold with the time it has left, and only the
    // pricing changes.
    mutationFn: (ticketTypeId: string | null) =>
      apiRequest<{ checkout: CheckoutResponse['checkout'] }>(
        `/api/event-orders/events/${encodeURIComponent(eventId)}/checkout`,
        { method: 'POST', body: ticketTypeId === null ? {} : { ticketTypeId } },
      ),
    onSuccess: (data, ticketTypeId) => {
      // Seeded rather than invalidated: the response *is* the answer the query would fetch, and
      // re-fetching would spend a round trip to be told the same thing while the member watches a
      // countdown that has not started.
      queryClient.setQueryData(checkoutKey(eventId, ticketTypeId), data);
    },
  });
}

/** Leave checkout and give the seat back early. Idempotent — see `EVT-022` AC-3. */
export function useAbandonCheckout(eventId: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiRequest<void>(`/api/event-orders/events/${encodeURIComponent(eventId)}/checkout`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      // Every ticket type's entry, because the seat is gone for all of them — one hold, one seat,
      // whichever tier the member was looking at when they left.
      queryClient.removeQueries({ queryKey: ['event-orders', 'checkout', eventId] });
    },
  });
}

/**
 * The receipt for a place this member holds.
 *
 * A `404` here is a final answer — the member holds no registration — so `retry: false`, and the
 * screen says so rather than spinning.
 */
export function useReceipt(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<ReceiptResponse> {
  return useQuery({
    queryKey: receiptKey(eventId),
    queryFn: () =>
      apiRequest<ReceiptResponse>(
        `/api/event-orders/events/${encodeURIComponent(eventId)}/receipt`,
      ),
    enabled: options.enabled && eventId.length > 0,
    retry: false,
  });
}

/**
 * **Pay for the seat this member is holding** (`MEM-021` AC-3, AC-4).
 *
 * A mutation, fired by an explicit act, and the **only** call in this application that moves
 * money. Its variable is one string: the token the provider's hosted elements produced in the
 * member's browser. Nothing else from the card form exists to send — there is no value for this
 * app to read (`card-elements.ts`) and the API's own body schema is `.strict()` with this one
 * field, so a card detail cannot reach a server log by way of a request that was accepted and
 * ignored.
 *
 * `retry: false`, and here it is load-bearing rather than a convention. react-query's default
 * retries a failed mutation's *network* errors, and a request that timed out after the provider
 * captured is exactly the case where a blind retry is a second charge. The server makes a repeat
 * safe — the order id is derived from the seat hold, so the provider replays its own answer — but
 * a client that retries automatically is a client asking to find out whether that is true.
 *
 * ## Its variable is three fields, and only one of them is the token
 *
 * `ticketTypeId` says **which ticket** — the seat hold between checkout and payment holds a seat
 * rather than a tier, so the purchase has to name it. `agreedTicketPriceCents` is **the price the
 * member read**, taken off the itemisation the server served rather than composed here, and the
 * server compares it to the event's own before it charges anything: an organizer who edits the
 * price while a member is finding their card gets that member a refusal and a new total to look
 * at rather than a surprise on their statement. Neither field can charge anything and neither is
 * a card detail; the API's `.strict()` schema still refuses all four of those.
 *
 * On success the checkout and the registration are both stale: the seat is now a registration, so
 * the screens that ask "am I going?" have to be told. The receipt is fetched by the screen the
 * member lands on.
 */
export function usePayForCheckout(
  eventId: string,
): UseMutationResult<PaymentResponse, Error, PayForCheckout> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paying: PayForCheckout) =>
      apiRequest<PaymentResponse>(
        `/api/event-orders/events/${encodeURIComponent(eventId)}/payment`,
        {
          method: 'POST',
          body: {
            paymentMethodToken: paying.paymentMethodToken,
            ...(paying.ticketTypeId === null ? {} : { ticketTypeId: paying.ticketTypeId }),
            agreedTicketPriceCents: paying.agreedTicketPriceCents,
            agreedRefundTermsFingerprint: paying.agreedRefundTermsFingerprint,
          },
        },
      ),
    retry: false,
    onSuccess: async () => {
      /*
        The spent checkout is **removed** rather than invalidated: the hold is gone, and refetching
        it would mint a new one for a member who has just paid. It has to come off before the
        refresh below, which would otherwise put it straight back.
      */
      queryClient.removeQueries({ queryKey: ['event-orders', 'checkout', eventId] });
      /*
        **And then everything a registration decides** — the roster, the meet list, the match set,
        the ticket, Your Events (`registration-dependent-reads.ts`). This line was
        `invalidateQueries({ queryKey: ['event-registrations', eventId] })`, which is **not a key
        this app has**: the registration read is keyed `['event-registrations', 'event', eventId]`,
        so the prefix matched nothing and the paid path refreshed neither the roster nor the
        registration panel the free path did remember. Found on 2026-09-12 while fixing the free
        path's own version of the same defect.
      */
      await refreshRegistrationDependentReads(queryClient);
    },
  });
}

/**
 * **Which provider elements this build has**, and the honest answer when it has none (ADR 045).
 *
 * ## Why there is a stand-in at all, and what it is not
 *
 * `.env.example` promises that with `PAYMENT_PROVIDER=mock` *"a checkout can be driven end to end
 * with no provider credentials"*, and the server half already keeps that promise:
 * `createMockPaymentAdapter` authorises, captures and refunds for real, in memory, with real
 * idempotency. Without a client-side counterpart the promise stops at the browser — a developer or
 * a PM opening `/app/events/:id/checkout` would meet a stated absence and never reach the code the
 * story is about.
 *
 * So the mock host below draws **no card fields at all**. It mounts a sentence saying no card is
 * being collected and answers a fixed token. That is deliberate and it is the whole point: a
 * development stand-in that drew inputs would be inputs whose values this application holds, which
 * is the one thing `MEM-021` AC-3 forbids and the one thing the real integration never does. What
 * it stands in for is the *exchange*, not the collection.
 *
 * ## The real host, and the three answers this function can give (`PAY-004`)
 *
 * Until `PAY-004` there was no Stripe host at all, and this function answered `null` everywhere
 * outside a mock build — so a member on the sandbox met a stated absence and could not pay. There
 * are now three answers, and the order they are asked in matters:
 *
 * 1. **`VITE_PAYMENT_PROVIDER=mock` → the stand-in.** Checked *first*, so an explicit mock build
 *    stays a mock build even on a machine that happens to have a publishable key in its
 *    environment. A developer who asked for test mode gets test mode; the alternative is a `.env`
 *    left over from another branch quietly reaching a real provider account.
 * 2. **A publishable key is configured → the provider's own iframed fields.** This is the
 *    deployed path, and `apps/web/.env.dev` carries the sandbox's key.
 * 3. **Neither → `null`, unchanged.** The honest refusal `MEM-020` and ADR 045 both ask for: the
 *    checkout says card payment is not available rather than drawing a form that cannot work.
 *    ADR 045's rule read from the client side is *"an adapter that looks like it took the money is
 *    worse than one that crashes"*, and a form that looks like it took a card is that shape.
 *
 * Constructing the Stripe host costs nothing and cannot fail — it fetches no script and builds no
 * client until a field is actually mounted — which matters because this runs as a default prop
 * while a checkout renders. That is `createDeferredPaymentPort`'s argument (ADR 045, amended
 * 2026-08-29) on the client side: resolving a payment capability must never be what breaks a
 * screen.
 */
export function resolveCardElements(): CardElementsHost | null {
  if (isMockPaymentBuild()) return createMockCardElements();
  const publishableKey = getPaymentPublishableKey();
  return publishableKey === null ? null : createStripeCardElements(publishableKey);
}

/** `VITE_PAYMENT_PROVIDER=mock`, read through the same defensive path every other variable is. */
function isMockPaymentBuild(): boolean {
  const environment = import.meta.env as Record<string, string | undefined> | undefined;
  return environment?.['VITE_PAYMENT_PROVIDER'] === 'mock';
}

/**
 * The development host: it collects nothing and exchanges nothing.
 *
 * The token it answers with is the one `@h2hspark/payments`' mock adapter recognises, so a local
 * checkout runs the real payment service, the real order row, the real ledger claim and the real
 * confirmation — every mechanism this story is about except the provider itself.
 */
export function createMockCardElements(): CardElementsHost {
  return {
    mount: (field, node) => {
      node.textContent = MOCK_CARD_FIELD_NOTICE;
      node.setAttribute('data-mock-card-field', field);
      return () => {
        node.textContent = '';
      };
    },
    // Always complete: there is nothing to fill in, so a Pay control that stayed disabled would
    // make the stand-in useless for the thing it exists for.
    onValidityChange: (listener) => {
      listener({ complete: true, message: MOCK_CARD_ENTRY_NOTICE });
      return () => undefined;
    },
    // Takes the name the panel now collects (`MEM-021` AC-2, ruled 2026-08-30) and deliberately
    // does nothing with it. The parameter is named rather than omitted so the two implementations
    // stay honest about the same contract — a stand-in whose signature quietly drifted from the
    // real host is a stand-in that stops proving anything about it — and it is dropped rather
    // than echoed back, because a development build is exactly where a value gets kept "just to
    // see it" and then stays.
    createPaymentMethodToken: async (_details) => MOCK_PAYMENT_METHOD_TOKEN,
  };
}

// ─── what a member reads back afterwards (`MEM-022` AC-3 … AC-7) ───────────────────────────────

const MY_REGISTERED_EVENTS_KEY = ['event-orders', 'mine'] as const;
const orderKey = (orderId: string): readonly unknown[] => ['event-orders', 'order', orderId];
const cancellationPreviewKey = (eventId: string): readonly unknown[] => [
  'event-orders',
  'cancellation-preview',
  eventId,
];

/**
 * **What would come back if this member cancelled now** (`MEM-023` AC-8).
 *
 * A query, because it is a read: the server decides the case and reads the figures off the
 * order's stamp, and nothing moves. The confirmation dialog renders the served sentence before
 * its destructive control, and the control is withheld until this has answered — a member who
 * confirms before reading what comes back has been told the deal after taking it.
 *
 * `staleTime: 0`, so a member who opens the dialog, keeps their place, and opens it again a day
 * later is judged again: a cut-off can pass in between, and the sentence has to say so.
 */
export function useCancellationPreview(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<CancellationPreviewResponse> {
  return useQuery({
    queryKey: cancellationPreviewKey(eventId),
    queryFn: () =>
      apiRequest<CancellationPreviewResponse>(
        `/api/event-orders/events/${encodeURIComponent(eventId)}/cancellation-preview`,
      ),
    enabled: options.enabled && eventId.length > 0,
    staleTime: 0,
    retry: false,
  });
}

/**
 * **Every event this member registered for** (`MEM-022` AC-3, AC-6).
 *
 * ## One hook, and that is AC-6
 *
 * `MEM-TKT-015` requires the web's *Your registrations* block and the PWA's *Tickets & orders*
 * screen to render **the same data**, and the story's Notes say what happens if that is left
 * to discipline: *"Two renderers over one source; not two sources."*
 *
 * This is the one source. Both surfaces call this hook, under one query key, against one route —
 * so the two cannot fall out of step by fetching different things, and a member who opens both in
 * one session is served from one cache entry rather than from two requests that could disagree.
 *
 * **The rows are registrations now** (Sumit, 2026-09-03), and the route and the key did not move:
 * the same `/api/event-orders/mine` answers `{ registrations }` where it used to answer
 * `{ orders }`. A member who attended a free event had no order and therefore no row, on a block
 * headed *"Events you've registered for."*
 *
 * `retry: false`, matching every other member-owned read in this app: a 401 here is a final answer
 * and retrying it three times only delays the sign-in prompt.
 */
export function useMyRegisteredEvents(options: {
  readonly enabled: boolean;
}): UseQueryResult<MyRegisteredEventsResponse> {
  return useQuery({
    queryKey: MY_REGISTERED_EVENTS_KEY,
    queryFn: () => apiRequest<MyRegisteredEventsResponse>('/api/event-orders/mine'),
    enabled: options.enabled,
    retry: false,
  });
}

/**
 * **One order, if it is this member's** (`MEM-022` AC-4, AC-5, AC-7).
 *
 * The id travels in the path, and that is the only place in this slice where a member names a
 * resource rather than being it. **Nothing here checks ownership and nothing here should**: the
 * server answers `404` for another member's order — indistinguishable from an order that does not
 * exist — and a client-side check would be a second opinion that could disagree with it, on the
 * side of the caller.
 *
 * So a screen built on this renders exactly two states: the order, or the not-found empty state.
 * There is no "you may not see this", because the client is never told that.
 */
export function useMyOrder(
  orderId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<MyOrderResponse> {
  return useQuery({
    queryKey: orderKey(orderId),
    queryFn: () =>
      apiRequest<MyOrderResponse>(`/api/event-orders/orders/${encodeURIComponent(orderId)}`),
    enabled: options.enabled && orderId.length > 0,
    retry: false,
  });
}

/**
 * What paying takes: the provider's token, which ticket, and the price the member agreed to.
 *
 * Three fields rather than a bare string, because two of them are the *purchase* and getting
 * either wrong is a member charged for something they did not read. The type is here rather than
 * in `types.ts` because it is a request shape this app composes, not a wire shape it receives.
 */
export interface PayForCheckout {
  readonly paymentMethodToken: string;
  /** `null` for the organizer's first ticket type — what the pinned reserve bar named. */
  readonly ticketTypeId: string | null;
  /** The ticket line's own `amountCents`, off the itemisation this screen rendered. */
  readonly agreedTicketPriceCents: number;
  /**
   * `Checkout.refundTermsFingerprint`, echoed unchanged — the terms this screen showed under
   * *Refunds*, as the server described them.
   *
   * The price guard's twin, one field over. An organizer who tightens the refund policy while a
   * member is finding their card gets that member a refusal and the new terms to read, rather than
   * a purchase made under conditions they never saw.
   */
  readonly agreedRefundTermsFingerprint: string;
}
